/**
 * KpiGrid - The Daily 9 KPI metrics display
 * CHANGED: Now fetches from /api/metrics instead of hardcoded values.
 * Falls back to seeded defaults when no API data is available yet.
 * OpenClaw agents: can POST to /api/metrics to update KPI values.
 */
'use client';

import React, { useCallback } from 'react';
import { ArrowUpRight, ArrowDownRight, DollarSign, Sprout, TrendingUp, AlertCircle } from 'lucide-react';
import { usePolling } from '@/lib/usePolling';
import type { KPICardProps } from '@/lib/types';

interface MetricRow {
  kpi_slug: string;
  kpi_label: string;
  value: number;
  target_value: number;
  status: 'healthy' | 'warning' | 'critical';
}

// Default KPIs shown when the database has no data yet.
// These serve as the schema for what OpenClaw agents should populate.
const DEFAULT_METRICS: KPICardProps[] = [
  { label: 'CAC (Blended)', value: 0, target: 120, status: 'warning', trend: 0, prefix: '$' },
  { label: 'LTV/CAC Ratio', value: 0, target: 5.0, status: 'warning', trend: 0 },
  { label: 'Lead Velocity', value: 0, target: 30, status: 'warning', trend: 0, suffix: '/day' },
  { label: 'Soil Temp (KC)', value: 0, target: 55, status: 'warning', trend: 0, suffix: '\u00B0F' },
  { label: 'Contract Closures', value: 0, target: 100, status: 'warning', trend: 0, suffix: '%' },
  { label: 'Revenue Pacing', value: 0, target: 100, status: 'warning', trend: 0, prefix: '$', suffix: 'k' },
  { label: 'Labor Variance', value: 0, target: 5, status: 'warning', trend: 0, suffix: '%' },
  { label: 'Commercial Backlog', value: 0, target: 20, status: 'warning', trend: 0, prefix: '$', suffix: 'k' },
  { label: 'Token Efficiency', value: 0, target: 10, status: 'warning', trend: 0, prefix: 'x' },
];

// Map from kpi_slug to display config
const SLUG_CONFIG: Record<string, { prefix?: string; suffix?: string }> = {
  cac_blended: { prefix: '$' },
  ltv_cac_ratio: {},
  lead_velocity: { suffix: '/day' },
  soil_temp_kc: { suffix: '\u00B0F' },
  contract_closures: { suffix: '%' },
  revenue_pacing: { prefix: '$', suffix: 'k' },
  labor_variance: { suffix: '%' },
  commercial_backlog: { prefix: '$', suffix: 'k' },
  token_efficiency: { prefix: 'x' },
};

/** Format a KPI number: integers stay whole, decimals get 1 place max */
function fmtNum(n: number): string {
  if (n == null || isNaN(n)) return '0';
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function metricsToKPI(rows: MetricRow[]): KPICardProps[] {
  if (!rows || rows.length === 0) return DEFAULT_METRICS;

  return rows.map(row => {
    const config = SLUG_CONFIG[row.kpi_slug] || {};
    const pct = row.target_value > 0 ? Math.round((row.value / row.target_value) * 100) : 0;
    return {
      label: row.kpi_label,
      value: row.value,
      target: row.target_value,
      status: row.status,
      trend: Math.round(pct * 10) / 10,
      ...config,
    };
  });
}

const KPICard = React.memo(function KPICard({ label, value, target, status, trend, prefix = "", suffix = "" }: KPICardProps) {
  const isHealthy = status === 'healthy';
  const isCritical = status === 'critical';
  const noData = value === 0 && trend === 0;

  return (
    <div className="bg-zinc-900/50 border border-white/5 backdrop-blur-md rounded-2xl p-5 hover:border-zinc-700 transition-colors group">
      <div className="flex justify-between items-start mb-4">
        <label className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400 group-hover:text-zinc-200 transition-colors">
          {label}
        </label>
        <div className={`p-1.5 rounded-lg ${noData ? 'bg-zinc-800/50 text-zinc-500' :
          isHealthy ? 'bg-emerald-500/10 text-emerald-400' :
            isCritical ? 'bg-rose-500/10 text-rose-500' :
              'bg-amber-500/10 text-amber-500'
          }`}>
          {noData ? <AlertCircle className="w-3 h-3" /> :
            label.includes('CAC') ? <DollarSign className="w-3 h-3" /> :
              label.includes('Soil') ? <Sprout className="w-3 h-3" /> :
                <TrendingUp className="w-3 h-3" />}
        </div>
      </div>

      <div className="flex items-baseline gap-2">
        {noData ? (
          <span className="text-lg font-mono text-zinc-500">Awaiting data</span>
        ) : (
          <>
            <span className="text-3xl font-black text-white tracking-tighter tabular-nums">
              {prefix}{fmtNum(value)}{suffix}
            </span>
            <span className="text-[10px] font-mono text-zinc-400 tabular-nums">
              target: {prefix}{fmtNum(target)}{suffix}
            </span>
          </>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div className="h-1 flex-1 bg-zinc-800 rounded-full overflow-hidden mr-3">
          <div
            className={`h-full transition-colors duration-1000 ${noData ? 'bg-zinc-700' :
              isHealthy ? 'bg-emerald-500' : isCritical ? 'bg-rose-500' : 'bg-amber-500'
              }`}
            style={{ width: noData ? '0%' : `${Math.min(100, (value / target) * 100)}%` }}
          />
        </div>
        {!noData && (
          <div className={`flex items-center gap-0.5 text-[11px] font-bold px-1.5 py-0.5 rounded tabular-nums ${trend >= 100 ? 'text-emerald-400 bg-emerald-500/10' : 'text-rose-400 bg-rose-500/10'}`}>
            {trend >= 100 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {trend}%
          </div>
        )}
      </div>
    </div>
  );
});

export const KpiGrid = () => {
  const { data: metrics } = usePolling<KPICardProps[]>({
    url: '/api/metrics',
    intervalMs: 30000,
    transform: useCallback((json: Record<string, any>) => {
      const rows = Array.isArray(json) ? json : Array.isArray(json?.metrics) ? json.metrics : [];
      return metricsToKPI(rows);
    }, []),
    initialValue: DEFAULT_METRICS,
  });

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-12">
      {metrics.map((m, i) => (
        <KPICard key={i} {...m} />
      ))}
    </div>
  );
};
